// RecentActivity — Portfolio's "Recent fund activity" card. Reuses the shared
// activity-card styling (src/styles/activity-card.css) the Dashboard uses — the
// design borrows the same .activity-* classes — in a compact 3-col variant
// (icon · ticker+desc · amount), with a "View all transactions" footer.
// Presentational. Source of truth: portfolio3.jsx RecentFundActivity.
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Surface, Skeleton, EmptyState } from '../../../reusable';
import { cx } from '../../../../utils/cx';
import { formatCurrency } from '../../../../utils/format';
import { DepositIcon, RefreshIcon, DividendIcon, SwapIcon, ArrowRightIcon, InboxIcon } from '../../../icons/Icons';
import './recent-activity.css';

// type → icon component + the shared .activity-ic--* color variant.
const TYPES = {
  contribution: { Icon: DepositIcon, ic: 'contribution' },
  rebalance: { Icon: RefreshIcon, ic: 'rebalance' },
  dividend: { Icon: DividendIcon, ic: 'dividend' },
  transfer: { Icon: SwapIcon, ic: 'match' },
};

// Statuses that mean "not settled yet" — mirrors the adapter's PENDING_STATUSES so
// a row flagged pending in the hero reads pending here too (FM-2098).
const PENDING_STATUSES = new Set(['pending', 'processing', 'submitted', 'in_progress']);
const isPendingRow = (a) => PENDING_STATUSES.has(String(a.status || '').toLowerCase());

const amountText = (a) => {
  if (a.amount == null) return '—';
  const sign = a.signed && a.amount >= 0 ? '+' : '';
  return `${sign}${formatCurrency(a.amount)}`;
};

// '' → base color; 'pos' → green; 'na' → muted italic (shared variants). A pending
// row never shows green — the money isn't settled, so it stays base color and the
// pending tag carries the meaning (avoids the hero-vs-list mismatch, FM-2098).
const amountTone = (a) => (a.amount == null ? 'na' : isPendingRow(a) ? '' : a.signed ? 'pos' : '');

export function RecentActivity({ items = [], onViewAll, loading = false }) {
  const { t } = useTranslation();
  return (
    <Surface className="activity-card activity-card--compact" padding="none">
      <div className="activity-head">
        <span className="activity-head__title">{t('portfolio.activity.title')}</span>
        <span className="activity-asof">{t('portfolio.activity.last30')}</span>
      </div>

      {loading ? (
        <div className="activity-list" aria-busy="true">
          <Skeleton variant="text" count={5} />
        </div>
      ) : items.length === 0 ? (
        <div className="activity-empty">
          <EmptyState icon={<InboxIcon />} title={t('portfolio.activity.emptyTitle')} message={t('portfolio.activity.emptyMessage')} />
        </div>
      ) : (
        <div className="activity-list">
          {items.map((a) => {
            const { Icon, ic } = TYPES[a.type] || TYPES.contribution;
            const tone = amountTone(a);
            return (
              <div key={a.id} className="activity-row" role="listitem">
                <span className={cx('activity-ic', `activity-ic--${ic}`)}>
                  <Icon width={15} height={15} />
                </span>
                <span className="activity-text">
                  <span className="activity-h">
                    {/* A rolled-up multi-fund transaction has no single ticker (FM-2718). */}
                    {a.ticker && (
                      <>
                        <span className="activity-tkr">{a.ticker}</span>
                        <span className="activity-sep"> · </span>
                      </>
                    )}
                    {a.desc}
                    {isPendingRow(a) && (
                      <span className="activity-status activity-status--pending activity-pending-tag">
                        {t(
                          String(a.status).toLowerCase() === 'processing'
                            ? 'portfolio.activity.processing'
                            : 'portfolio.activity.pending',
                        )}
                      </span>
                    )}
                  </span>
                  <span className="activity-d">{a.date}</span>
                </span>
                <span className={cx('activity-amt', tone && `activity-amt--${tone}`)}>
                  {amountText(a)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div className="activity-foot">
        <button type="button" onClick={onViewAll}>
          {t('portfolio.activity.viewAll')} <ArrowRightIcon width={12} height={12} />
        </button>
      </div>
    </Surface>
  );
}

RecentActivity.propTypes = {
  /** [{ id, type, ticker, desc, date, amount:number|null, signed:bool, status?:string }] */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.string,
      ticker: PropTypes.string,
      desc: PropTypes.string,
      date: PropTypes.string,
      amount: PropTypes.number,
      signed: PropTypes.bool,
      // Settlement status; when pending/processing the row shows a tag and the
      // amount is not colored green.
      status: PropTypes.string,
    }),
  ),
  onViewAll: PropTypes.func,
  loading: PropTypes.bool,
};

export default RecentActivity;
